import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { type Roadmap, type RoadmapStatus } from "@/types";
import { toast } from "sonner";

export const useRoadmapData = (companyId: string | undefined) => {
  const [roadmaps, setRoadmaps] = useState<Roadmap[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRoadmapId, setSelectedRoadmapId] = useState<string | null>(null);

  const fetchRoadmaps = useCallback(async () => {
    if (!companyId) return;
    
    try {
      setIsLoading(true);
      console.log("Fetching roadmaps for company:", companyId);
      
      const { data: roadmapsData, error: roadmapsError } = await supabase
        .from('roadmaps')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: true });

      if (roadmapsError) {
        console.error('Error fetching roadmaps:', roadmapsError);
        toast.error('Failed to load roadmaps');
        return;
      }

      console.log("Roadmaps data fetched:", roadmapsData);
      
      if (roadmapsData && roadmapsData.length > 0) {
        const roadmapsWithStatuses = await Promise.all(
          roadmapsData.map(async (roadmap) => {
            const { data: statusesData, error: statusesError } = await supabase
              .from('roadmap_statuses')
              .select('*')
              .eq('roadmap_id', roadmap.id)
              .order('position', { ascending: true });

            if (statusesError) {
              console.error(`Error fetching statuses for roadmap ${roadmap.id}:`, statusesError);
              return {
                ...roadmap,
                statuses: []
              };
            }

            return {
              ...roadmap,
              statuses: statusesData || []
            };
          })
        );

        setRoadmaps(roadmapsWithStatuses);
        
        const defaultRoadmap = roadmapsWithStatuses.find(r => r.is_default);
        setSelectedRoadmapId(defaultRoadmap?.id || roadmapsWithStatuses[0]?.id || null);
      } else {
        setRoadmaps([]);
        setSelectedRoadmapId(null);
      }
    } catch (error) {
      console.error('Error in fetchRoadmaps:', error);
      toast.error('Failed to load roadmaps');
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  const createRoadmap = async (data: { name: string; description?: string }) => {
    if (!companyId) return null;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in to create a roadmap');
        return null;
      }
      
      const { data: roadmap, error } = await supabase
        .from('roadmaps')
        .insert({
          company_id: companyId,
          name: data.name,
          description: data.description || null,
          created_by: user.id,
          is_default: false
        })
        .select()
        .single();
      
      if (error) {
        console.error('Error creating roadmap:', error);
        toast.error('Failed to create roadmap');
        return null;
      }
      
      const statusData = [
        { name: 'In Review', color: '#9333ea', description: 'Ideas being considered', position: 1 },
        { name: 'Planned', color: '#3b82f6', description: 'Accepted for future development', position: 2 },
        { name: 'In Progress', color: '#eab308', description: 'Currently being worked on', position: 3 },
        { name: 'Completed', color: '#22c55e', description: 'Successfully implemented', position: 4 },
        { name: 'Rejected', color: '#ef4444', description: 'Not being implemented', position: 5 }
      ];
      
      const { error: statusError } = await supabase
        .from('roadmap_statuses')
        .insert(
          statusData.map(status => ({
            roadmap_id: roadmap.id,
            ...status
          }))
        );
      
      if (statusError) {
        console.error('Error creating default statuses:', statusError);
        toast.error('Failed to create default statuses');
      }
      
      await fetchRoadmaps();
      toast.success('Roadmap created successfully');
      return roadmap;
    } catch (error) {
      console.error('Error in createRoadmap:', error);
      toast.error('Failed to create roadmap');
      return null;
    }
  };

  const updateRoadmap = async (roadmapId: string, data: { name: string; description?: string }) => {
    try {
      const { error } = await supabase
        .from('roadmaps')
        .update({
          name: data.name,
          description: data.description
        })
        .eq('id', roadmapId);
      
      if (error) {
        console.error('Error updating roadmap:', error);
        toast.error('Failed to update roadmap');
        return false;
      }
      
      await fetchRoadmaps();
      toast.success('Roadmap updated successfully');
      return true;
    } catch (error) {
      console.error('Error in updateRoadmap:', error);
      toast.error('Failed to update roadmap');
      return false;
    }
  };

  const deleteRoadmap = async (roadmapId: string) => {
    try {
      if (roadmaps.length <= 1) {
        toast.error('Cannot delete the only roadmap');
        return false;
      }
      
      const roadmapToDelete = roadmaps.find(r => r.id === roadmapId);
      if (roadmapToDelete?.is_default) {
        toast.error('Cannot delete the default roadmap');
        return false;
      }

      const { error } = await supabase
        .from('roadmaps')
        .delete()
        .eq('id', roadmapId);
      
      if (error) {
        console.error('Error deleting roadmap:', error);
        toast.error('Failed to delete roadmap');
        return false;
      }
      
      await fetchRoadmaps();
      toast.success('Roadmap deleted successfully');
      return true;
    } catch (error) {
      console.error('Error in deleteRoadmap:', error);
      toast.error('Failed to delete roadmap');
      return false;
    }
  };

  const setDefaultRoadmap = async (roadmapId: string) => {
    try {
      await supabase
        .from('roadmaps')
        .update({ is_default: false })
        .eq('company_id', companyId);
      
      const { error } = await supabase
        .from('roadmaps')
        .update({ is_default: true })
        .eq('id', roadmapId);
      
      if (error) {
        console.error('Error setting default roadmap:', error);
        toast.error('Failed to set default roadmap');
        return false;
      }
      
      await fetchRoadmaps();
      toast.success('Default roadmap updated');
      return true;
    } catch (error) {
      console.error('Error in setDefaultRoadmap:', error);
      toast.error('Failed to set default roadmap');
      return false;
    }
  };

  const createStatus = async (roadmapId: string, data: { name: string; color: string; description?: string }) => {
    try {
      const { data: statuses } = await supabase
        .from('roadmap_statuses')
        .select('position')
        .eq('roadmap_id', roadmapId)
        .order('position', { ascending: false })
        .limit(1);
      
      const nextPosition = statuses && statuses.length > 0 ? statuses[0].position + 1 : 1;
      
      const { error } = await supabase
        .from('roadmap_statuses')
        .insert({
          roadmap_id: roadmapId,
          name: data.name,
          color: data.color,
          description: data.description || null,
          position: nextPosition
        });
      
      if (error) {
        console.error('Error creating status:', error);
        toast.error('Failed to create status');
        return false;
      }
      
      await fetchRoadmaps();
      toast.success('Status created successfully');
      return true;
    } catch (error) {
      console.error('Error in createStatus:', error);
      toast.error('Failed to create status');
      return false;
    }
  };

  const updateStatus = async (statusId: string, data: { name: string; color: string; description?: string }) => {
    try {
      const { error } = await supabase
        .from('roadmap_statuses')
        .update({
          name: data.name,
          color: data.color,
          description: data.description
        })
        .eq('id', statusId);
      
      if (error) {
        console.error('Error updating status:', error);
        toast.error('Failed to update status');
        return false;
      }
      
      await fetchRoadmaps();
      toast.success('Status updated successfully');
      return true;
    } catch (error) {
      console.error('Error in updateStatus:', error);
      toast.error('Failed to update status');
      return false;
    }
  };

  const deleteStatus = async (statusId: string, roadmapId: string) => {
    try {
      const roadmap = roadmaps.find(r => r.id === roadmapId);
      if (roadmap && roadmap.statuses.length <= 1) {
        toast.error('Cannot delete the only status');
        return false;
      }

      const { error } = await supabase
        .from('roadmap_statuses')
        .delete()
        .eq('id', statusId);
      
      if (error) {
        console.error('Error deleting status:', error);
        toast.error('Failed to delete status');
        return false;
      }
      
      await fetchRoadmaps();
      toast.success('Status deleted successfully');
      return true;
    } catch (error) {
      console.error('Error in deleteStatus:', error);
      toast.error('Failed to delete status');
      return false;
    }
  };

  const reorderStatuses = async (roadmapId: string, orderedStatusIds: string[]) => {
    try {
      const { data: existingStatuses, error: fetchError } = await supabase
        .from('roadmap_statuses')
        .select('*')
        .eq('roadmap_id', roadmapId);
      
      if (fetchError) {
        console.error('Error fetching statuses:', fetchError);
        toast.error('Failed to reorder statuses');
        return false;
      }
      
      const statusMap = new Map(
        existingStatuses?.map(status => [status.id, status]) || []
      );
      
      const updates = orderedStatusIds.map((statusId, index) => {
        const existingStatus = statusMap.get(statusId);
        if (!existingStatus) {
          console.error(`Status with id ${statusId} not found`);
          return null;
        }
        
        return {
          id: statusId,
          roadmap_id: existingStatus.roadmap_id,
          name: existingStatus.name,
          color: existingStatus.color,
          description: existingStatus.description,
          position: index + 1
        };
      }).filter(Boolean) as { 
        id: string; 
        roadmap_id: string; 
        name: string; 
        color: string; 
        description: string | null; 
        position: number; 
      }[];
      
      const { error } = await supabase
        .from('roadmap_statuses')
        .upsert(updates, { onConflict: 'id' });
      
      if (error) {
        console.error('Error reordering statuses:', error);
        toast.error('Failed to reorder statuses');
        return false;
      }
      
      await fetchRoadmaps();
      toast.success('Statuses reordered successfully');
      return true;
    } catch (error) {
      console.error('Error in reorderStatuses:', error);
      toast.error('Failed to reorder statuses');
      return false;
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchRoadmaps();
    }
  }, [companyId, fetchRoadmaps]);

  const selectedRoadmap = roadmaps.find(r => r.id === selectedRoadmapId) || null;

  return { 
    roadmaps, 
    isLoading, 
    selectedRoadmapId, 
    setSelectedRoadmapId,
    selectedRoadmap,
    refetchRoadmaps: fetchRoadmaps,
    createRoadmap,
    updateRoadmap,
    deleteRoadmap,
    setDefaultRoadmap,
    createStatus,
    updateStatus,
    deleteStatus,
    reorderStatuses
  };
};
